import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigationType, useNavigate } from 'react-router';
import { flushSync } from 'react-dom';
import { toast } from 'react-hot-toast';
import { debounce } from 'lodash-es';

// Import styles
import 'react-day-picker/dist/style.css';

// Import constants
import {
  INITIAL_END_HOURS,
  INITIAL_START_HOURS,
  MAX_PARTICIPANTS,
  MAX_TITLE_LENGTH,
} from '../constants/schedule';

// Import utils
import {
  getQuarterHours,
  parseYmd,
  addDaysYmd,
  toYmd,
  diffDaysYmd,
  formatTimezoneWithOffset,
  hourToTimeString,
  timeStringToHour,
  normalizeTimeString,
} from '../utils/date';
import { logger } from '../utils/logger';
import { createRoomLink } from '../utils/linking';
import { saveStoredPlanner } from '../utils/schedule-storage';

// Import types
import type { EventByUser, HoveredCell } from '../types/schedule';

// Import hooks
import useRoomBootstrap from '../hooks/schedule/useRoomBootstrap';
import useTimezoneMode from '../hooks/timezone/useTimezoneMode';
import useParticipantsDeviceId from '../hooks/device/useParticipantsDeviceId';
import useParticipants from '../hooks/schedule/useParticipants';
import useEventModal from '../hooks/schedule/useEventModal';
import useRoomPersistence from '../hooks/schedule/useRoomPersistence';
import { useLinkedDevices } from '../hooks/device/useLinkedDevices';
import { useScheduleStore } from '../store/scheduleStore';

import useYmdDateKeyMap from '../hooks/schedule/useYmdDateKeyMap';
import useGridInteractions from '../hooks/schedule/useGridInteractions';
import useOverlapTimes from '../hooks/schedule/useOverlapTimes';
import usePointerCoarse from '../hooks/ui/usePointerCoarse';
import useHoverTooltip from '../hooks/ui/useHoverTooltip';
import useLeaveSiteAlert from '../hooks/ui/useLeaveSiteAlert';
import HoverTooltip from '../components/schedule/HoverTooltip';

// Import components
import Navbar from '../components/navbar/Navbar';
import Switch from '../components/switch/Switch';
import AvailabilityGrid from '../components/schedule/AvailabilityGrid';
import AvailabilityLegendAndHover from '../components/schedule/AvailabilityLegendAndHover';
import EventHeader from '../components/schedule/EventHeader';
import EventActions from '../components/schedule/EventActions';
import EventIdentity from '../components/schedule/EventIdentity';
import ParticipantsPanel from '../components/schedule/ParticipantsPanel';
import EventModal from '../components/schedule/EventModal';
import ScheduleTimezoneControls from '../components/schedule/ScheduleTimezoneControls';
import ScheduleEventModal from '../components/schedule/ScheduleEventModal';
import HelperBanner from '../components/schedule/HelperBanner';
import DeviceLinkingModal from '../components/device/DeviceLinkingModal';
import Footer from '../components/landing/Footer';
import SeoHead from '../components/seo/SeoHead';
import { SITE_NAME } from '../constants/seo';

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

function App() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();

  const { roomId } = useRoomBootstrap();

  // Store states
  const {
    // Event states
    savedRange,
    setSavedRange,

    // UI states
    title,
    setTitle,
    isOverlapTimes,
    setIsOverlapTimes,
    gridStart,
    gridEnd,
    setGridStart,
    setGridEnd,

    // Owner states
    ownerDeviceId,
    setOwnerDeviceId,
    ownerTimezone,
    setOwnerTimezone,
    maxParticipants,
    setMaxParticipants,

    // Modal states
    isScheduleEventMode,
    setIsScheduleEventMode,
    showScheduleModal,
    setShowScheduleModal,
    showLinkDevicesModal,
    setShowLinkDevicesModal,
  } = useScheduleStore();

  const [hoveredCell, setHoveredCell] = useState<HoveredCell | null>(null);

  const {
    timezone,
    setTimezone,
    hourMode,
    setHourMode,
    initialTZ,
    resetTimezone,
  } = useTimezoneMode();

  // Get linked devices for ownership checks
  const linkedDevicesResult = useLinkedDevices();
  const ownsParticipant = linkedDevicesResult.ownsParticipant;
  const isLinkedDevicesLoading = linkedDevicesResult.isLoading;

  // Participants device id sourced from localStorage first, then persistence
  const [participantsDeviceId, setParticipantsDeviceId] =
    useParticipantsDeviceId();

  const {
    users, // userIds
    currentUser, // userId
    selfNames,
    joinName,
    eventsByUser,
    userIdToName,
    ownerByUserId,
    setUsers,
    setUserIdToName,
    setOwnerByUserId,
    setJoinName,
    setCurrentUser,
    setEventsByUser,
    handleRemoveUser,
    handleJoin,
    handleRenameUser,
  } = useParticipants(participantsDeviceId, ownsParticipant, maxParticipants);

  // Configure Grid popup state
  const {
    showPopup,
    openPopup,
    cancelPopup,
    closePopup,
    eventTitle,
    setEventTitle,
    eventTimezone,
    setEventTimezone,
    pickerRange,
    setPickerRange,
    pickerStart,
    setPickerStart,
    pickerEnd,
    setPickerEnd,
  } = useEventModal(INITIAL_START_HOURS, INITIAL_END_HOURS);

  // Ownership (persisted by useRoomPersistence)

  // Room persistence hook wires storage/back-end and ownership
  const {
    isOwner,
    isDirty,
    saveNow,
    cancelChanges,
    notifyChanged,
    deviceId,
    isInitialLoading,
  } = useRoomPersistence({
    roomId,
    initialTZ,
    getSnapshot: () => ({
      users, // ids
      userIdToName,
      ownerByUserId,
      eventsByUser,
      gridStart: hourToTimeString(gridStart),
      gridEnd: hourToTimeString(gridEnd),
      savedRange,
      ownerDeviceId,
      ownerTimezone,
      eventTitle: title,
      maxParticipants,
    }),
    applySnapshot: (snap) => {
      logger.log('[DEBUG] applySnapshot:', snap);
      // Preserve a previously selected user when possible on cancel/apply
      const prevCurrent = currentUser;
      const nextUsers = snap.users || [];
      let nextCurrent =
        prevCurrent && nextUsers.includes(prevCurrent)
          ? prevCurrent
          : undefined;

      if (!nextCurrent) {
        // Prefer a user owned by this device (including linked devices), if any
        const ownedIds = nextUsers.filter((id) =>
          ownsParticipant(snap.ownerByUserId?.[id]),
        );
        if (ownedIds.length) nextCurrent = ownedIds[0];
      }

      if (!nextCurrent) nextCurrent = nextUsers[0] || '';

      setUsers(nextUsers);
      setCurrentUser(nextCurrent);
      setUserIdToName(snap.userIdToName || {});
      setOwnerByUserId(snap.ownerByUserId || {});
      setEventsByUser(snap.eventsByUser);
      setGridStart(timeStringToHour(snap.gridStart));
      setGridEnd(timeStringToHour(snap.gridEnd));
      setSavedRange(snap.savedRange);
      setOwnerDeviceId(snap.ownerDeviceId);
      setOwnerTimezone(snap.ownerTimezone);
      setTitle(snap.eventTitle || '');
      setMaxParticipants(snap.maxParticipants || MAX_PARTICIPANTS);
      // Mirror title and timezone in the popup draft store
      setEventTitle(snap.eventTitle || '');
      setEventTimezone(snap.ownerTimezone || initialTZ);

      // Mirror picker controls
      setPickerStart(snap.gridStart);
      setPickerEnd(snap.gridEnd);
      if (snap.savedRange) {
        const f = parseYmd(snap.savedRange.fromYmd);
        const t = parseYmd(snap.savedRange.toYmd);
        setPickerRange({
          from: new Date(f.y, f.m - 1, f.d),
          to: new Date(t.y, t.m - 1, t.d),
        });
      } else {
        setPickerRange({ from: undefined, to: undefined });
      }
    },
  });

  // Apply initial state from navigation state
  useEffect(() => {
    // Wait until any initial persistence load settles to avoid overwriting
    if (isInitialLoading) return;

    // Only apply when a user navigated here from LandingPage (PUSH),
    // not on refresh/back-forward (POP) or replace.
    // Do not apply if the room already has configuration
    if (navigationType !== 'PUSH') return;

    const init = location.state?.init;
    if (!init) return;
    logger.log('Applying initial room config from navigation state:', init);

    // Validate and transform incoming data
    const title = init.title ?? '';
    // Normalize time strings to HH:MM format and convert to numbers for gridStart/gridEnd
    const startTimeString = init.start
      ? normalizeTimeString(init.start)
      : hourToTimeString(gridStart);
    const endTimeString = init.end
      ? normalizeTimeString(init.end)
      : hourToTimeString(gridEnd);
    const startHour = timeStringToHour(startTimeString);
    const endHour = timeStringToHour(endTimeString);

    const pickerFrom = new Date(init.range.from);
    const pickerTo = new Date(init.range.to);
    const nextSavedRange = {
      fromYmd: toYmd(pickerFrom, timezone),
      toYmd: toYmd(pickerTo, timezone),
    };

    // Apply to state
    setTitle(title);
    setEventTitle(title);
    setGridStart(startHour);
    setGridEnd(endHour);
    setOwnerDeviceId(deviceId);
    setSavedRange(nextSavedRange);

    // Mirror picker controls for UI consistency (use normalized strings)
    setPickerStart(startTimeString);
    setPickerEnd(endTimeString);
    if (pickerFrom && pickerTo)
      setPickerRange({ from: pickerFrom, to: pickerTo });

    // Optionally adopt timezone from initializer
    if (typeof init.timezone === 'string' && init.timezone) {
      setTimezone(init.timezone);
      setOwnerTimezone(init.timezone);
    }

    // Clear the navigation state so refresh/back won't re-apply
    try {
      navigate(location.pathname, { replace: true, state: null });
    } catch (e) {
      logger.error('Failed to clear navigation state:', e);
    }

    // Save after the state has been flushed
    saveNow(init);
  }, [
    isInitialLoading,
    navigationType,
    location,
    ownerDeviceId,
    navigate,
    saveNow,
    setTimezone,
  ]);

  // Adopt persisted deviceId into participantsDeviceId after deviceId is initialized
  useEffect(() => {
    if (deviceId) setParticipantsDeviceId(deviceId);
  }, [deviceId, setParticipantsDeviceId]);

  // Bookmark this room locally when opened (same as mobile deep-link handling)
  useEffect(() => {
    if (!roomId) return;
    const url = createRoomLink(roomId);
    void saveStoredPlanner({
      id: roomId,
      url: url,
      createdAt: new Date().toISOString(),
    });
  }, [roomId]);

  // Keep dirty-state in sync when any persisted field changes
  useEffect(() => {
    notifyChanged();
  }, [
    notifyChanged,
    users,
    userIdToName,
    ownerByUserId,
    eventsByUser,
    gridStart,
    gridEnd,
    savedRange,
    ownerDeviceId,
    ownerTimezone,
    title,
  ]);

  // Build time grid (TIMES are exclusive of gridEnd)
  const TIMES = getQuarterHours(
    hourToTimeString(gridStart),
    hourToTimeString(gridEnd),
  );

  const actualYmds: string[] = useMemo(() => {
    const ymds: string[] = [];
    // Date columns (from savedRange YMD)
    if (savedRange?.fromYmd && savedRange.toYmd) {
      let cur = savedRange.fromYmd;
      ymds.push(cur);
      while (cur !== savedRange.toYmd) {
        cur = addDaysYmd(cur, 1);
        ymds.push(cur);
      }
    } else {
      // Default today in viewer timezone
      const todayYmd = toYmd(new Date(), timezone);
      ymds.push(todayYmd);
    }
    return ymds;
  }, [savedRange, timezone]);

  // Map the currently rendered columns' dateKey -> ymd for reliable reverse lookup
  const { map: ymdByDateKey, getYmd } = useYmdDateKeyMap(actualYmds);

  // Adapt store setter to React.Dispatch signature expected by useGridInteractions
  const setEventsByUserDispatch = useCallback<
    React.Dispatch<React.SetStateAction<EventByUser>>
  >(
    (value) => {
      if (typeof value === 'function') {
        const next = (value as (prev: EventByUser) => EventByUser)(
          eventsByUser,
        );
        setEventsByUser(next);
      } else {
        setEventsByUser(value);
      }
    },
    [eventsByUser, setEventsByUser],
  );

  // Grid interactions (drag/add/erase/hover)
  const interactions = useGridInteractions({
    users: users,
    currentUser: currentUser,
    ownerByUserId: ownerByUserId,
    deviceId: deviceId || participantsDeviceId,
    ownsParticipant: ownsParticipant,
    eventsByUser: eventsByUser,
    setEventsByUser: setEventsByUserDispatch,
    gridStart: hourToTimeString(gridStart),
    gridEnd: hourToTimeString(gridEnd),
    isOverlapTimes: isOverlapTimes,
    isScheduleEventMode: isScheduleEventMode,
  });

  // Overlapping-times derived data (counts per cell and denominator)
  const overlap = useOverlapTimes({
    users,
    eventsByUser,
    actualYmds,
    TIMES: interactions.TIMES,
  });

  // Tooltip state -------------------------------------------------------------

  const isCoarsePointer = usePointerCoarse();
  const { hoverInfo, startHover, stopHover, moveHover, clear } =
    useHoverTooltip({
      delayMs: 1500,
      disabled: isCoarsePointer,
    });

  // Immediate hover cell (for instant who-is-available summary)
  const [hoverCell, setHoverCell] = useState<{
    dateKey: string;
    row: number;
  } | null>(null);

  // When the timezone changes, clear any pending tooltip and reset the hover state.
  useEffect(() => {
    clear();
  }, [timezone]);

  // Also, clear immediate hover cell on timezone change to avoid stale cell indices
  useEffect(() => {
    setHoverCell(null);
  }, [timezone]);

  // When switching to a coarse pointer, ensure any pending tooltip is cleared
  useEffect(() => {
    if (isCoarsePointer) {
      clear();
    }
  }, [isCoarsePointer]);

  // Optional: when toggling Overlapping Times, we clear any active tooltip at once.
  useEffect(() => {
    clear();
    setHoverCell(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverlapTimes]);

  // Immediate hover handlers (independent of tooltip delay)
  const handleImmediateEnter = useCallback((dateKey: string, row: number) => {
    setHoverCell({ dateKey, row });
    // Also update hoveredCell for visual hover effect
    setHoveredCell({ dateKey, row });
  }, []);

  const handleGridLeaveImmediate = useCallback(() => {
    setHoverCell(null);
    setHoveredCell(null);
  }, []);

  // Track hover for visual effect
  const handleCellMouseEnter = useCallback((dateKey: string, row: number) => {
    setHoveredCell({ dateKey, row });
  }, []);

  const handleCellMouseLeave = useCallback(() => {
    setHoveredCell(null);
  }, []);

  // Schedule event handlers
  const handleScheduleEventClick = useCallback(() => {
    setIsScheduleEventMode(true);
    // Clear any existing selection
    interactions.setScheduleEventSelection(null);
  }, [interactions]);

  const handleScheduleEventComplete = useCallback(() => {
    if (interactions.scheduleEventSelection) {
      setShowScheduleModal(true);
    } else {
      toast.error('Please select a time slot on the grid');
    }
  }, [interactions.scheduleEventSelection]);

  const handleCloseScheduleModal = useCallback(() => {
    setShowScheduleModal(false);
    setIsScheduleEventMode(false);
    interactions.setScheduleEventSelection(null);
  }, [interactions]);

  const handleSave = useCallback(() => {
    // Validate owner-only fields
    if (!isOwner) {
      toast.error('Only the owner can edit the event');
      return;
    }

    if (eventTitle.length > MAX_TITLE_LENGTH) {
      toast.error(`Event title must be ${MAX_TITLE_LENGTH} characters or less`);
    }

    // Validate time range (at least 1 day)
    if (!pickerStart || !pickerEnd) {
      toast.error('Please select a start and end time');
    }

    // Validate start/end times are different
    if (pickerStart === pickerEnd) {
      toast.error('Start and end times cannot be the same');
      return;
    }

    // Validate a time format (HH:mm) from the time picker
    const sParts = pickerStart.split(':');
    const eParts = pickerEnd.split(':');
    const sh = Number(sParts[0]);
    const sm = Number(sParts[1]);
    const eh = Number(eParts[0]);
    const em = Number(eParts[1]);
    const startM = sh * 60 + sm;
    const endM = eh * 60 + em;

    // Validate time range (start before the end)
    if (endM <= startM) {
      toast.error('End time must be later than start time');
      return;
    }

    // Validate date range is selected
    if (!pickerRange?.from || !pickerRange?.to) {
      toast.error('Please select a date range');
      return;
    }

    // Validate date range is not selected past dates older than 3 months
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const from = new Date(pickerRange.from);
    from.setHours(0, 0, 0, 0);
    const to = new Date(pickerRange.to);
    to.setHours(0, 0, 0, 0);
    const min = new Date();
    min.setMonth(today.getMonth() - 3);
    if (from < min) {
      toast.error('You cannot select days before 3 months ago');
      return;
    }

    // Validate date range is within 10 days
    const msPerDay = 24 * 60 * 60 * 1000; // 24 hours
    const days = Math.floor((to.getTime() - from.getTime()) / msPerDay) + 1;
    if (days > 10) {
      toast.error('Please select at most 10 days');
      return;
    }

    // Compute YMDs for range validation and saving.
    // The event's saved range should be based on the event/owner timezone (set via the modal),
    // not the viewer timezone.
    const eventTZ = eventTimezone || timezone;
    const fromYmd = toYmd(from, eventTZ);
    const _toYmd = toYmd(to, eventTZ);
    const span = diffDaysYmd(fromYmd, _toYmd) + 1;
    if (span > 10) {
      toast.error('Please select at most 10 days');
      return;
    }

    // Commit all state changes synchronously so saveNow() sees the updated snapshot
    // This avoids requiring an extra Save click on the schedule page.
    flushSync(() => {
      setTitle(eventTitle);
      setOwnerTimezone(eventTimezone);
      setGridStart(timeStringToHour(pickerStart));
      setGridEnd(timeStringToHour(pickerEnd));
      setSavedRange({ fromYmd, toYmd: _toYmd });
      setOwnerDeviceId(deviceId);
    });

    // Save the changes and close the popup (no extra Save needed on the main page)
    saveNow();
    closePopup();
  }, [
    closePopup,
    deviceId,
    isOwner,
    pickerEnd,
    pickerRange.from,
    pickerRange.to,
    pickerStart,
    eventTitle,
    eventTimezone,
    saveNow,
    timezone,
  ]);

  const handleOpenEventModal = useCallback(() => {
    openPopup();
  }, [openPopup]);

  const handleCancelEventModal = useCallback(() => {
    cancelPopup();
  }, [cancelPopup]);

  // Debounce save handlers to avoid rapid double submissions
  const debouncedHeaderSave = useMemo(() => {
    // Fire immediately on the first click, ignore subsequent clicks within 600ms
    return debounce(
      () => {
        // Call the immediate save from persistence hook
        saveNow();
      },
      600,
      { leading: true, trailing: false },
    );
  }, [saveNow]);

  const debouncedModalSave = useMemo(() => {
    // Fire immediately on the first click, ignore subsequent clicks within 600ms
    return debounce(
      () => {
        handleSave();
      },
      600,
      { leading: true, trailing: false },
    );
  }, [handleSave]);

  // Cleanup debounced functions on unmount to prevent leaks
  useEffect(() => {
    return () => {
      debouncedHeaderSave.cancel();
      debouncedModalSave.cancel();
    };
  }, [debouncedHeaderSave, debouncedModalSave]);

  // Warn user before leaving page if there are unsaved changes
  useLeaveSiteAlert(isDirty);

  /* Initial loading overlay: shown while fetching the room from the backend or while device ID is being loaded */
  const isLoading = isInitialLoading || isLinkedDevicesLoading;

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
        <div className="flex items-center gap-3 text-gray-700">
          <div
            className="h-5 w-5 rounded-full border-2 border-gray-300 border-t-gray-700 animate-spin"
            aria-hidden="true"
          />
          <span className="text-sm">Loading…</span>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div aria-busy={isInitialLoading}>
      <SeoHead
        route="/s"
        canonicalPath={location.pathname}
        title={title.trim() ? `${title.trim()} - ${SITE_NAME}` : undefined}
      />
      {/* Navbar */}
      <div className="px-4 sm:px-6 lg:px-8 border-b border-gray-100/20 shadow-sm">
        <Navbar />
      </div>

      {/* Scheduler */}
      <div className="relative min-h-screen bg-white px-4 sm:px-6 lg:px-8">
        {/* Page header with title, date range and actions */}
        <div className="max-w-6xl mx-auto m-2 flex flex-col lg:flex-row md:items-center gap-3">
          <EventHeader
            eventTitle={title}
            isOwner={isOwner}
            ownerTimezone={ownerTimezone}
            timezone={timezone}
            hourMode={hourMode}
            gridStart={hourToTimeString(gridStart)}
            gridEnd={hourToTimeString(gridEnd)}
            savedRange={savedRange}
            actualYmds={actualYmds}
            openPopup={handleOpenEventModal}
          />

          <EventActions
            isDirty={isDirty}
            isScheduleEvent={isScheduleEventMode}
            onSave={debouncedHeaderSave}
            onCancel={cancelChanges}
            onScheduleEvent={handleScheduleEventClick}
          />
        </div>

        {/* Main content */}
        <div>
          <div className="max-w-6xl mx-auto">
            {/* Owner and viewing timezones */}
            <div className="flex flex-wrap justify-between items-center gap-2 text-sm">
              {isOwner ? (
                <span className="text-emerald-700 font-medium">
                  {ownerTimezone ? (
                    <>
                      Owner is in {formatTimezoneWithOffset(ownerTimezone)} |
                      Viewing in {formatTimezoneWithOffset(timezone)}
                    </>
                  ) : (
                    <>Viewing in {formatTimezoneWithOffset(timezone)}</>
                  )}
                </span>
              ) : (
                <span className="text-gray-500 font-medium">
                  {ownerTimezone ? (
                    <>
                      Owner is in {formatTimezoneWithOffset(ownerTimezone)} |
                      Viewing in {formatTimezoneWithOffset(timezone)}
                    </>
                  ) : (
                    <>Viewing in {formatTimezoneWithOffset(timezone)}</>
                  )}
                </span>
              )}

              {/* Link devices */}
              <button
                onClick={() => setShowLinkDevicesModal(true)}
                className="underline whitespace-nowrap text-green-700 hover:text-green-800"
                title="Link your devices to access meeting planners across platforms">
                Link Devices
              </button>
            </div>

            {/* Identity notice */}
            <EventIdentity
              selfNames={selfNames}
              viewingName={userIdToName[currentUser] || currentUser}
              isOverlapTimes={isOverlapTimes}
              isScheduleEventMode={isScheduleEventMode}
              isOwner={isOwner}
            />
          </div>

          {/* Main content: grid on the left, participants on the right */}
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-start gap-4 lg:gap-6">
            {/* Availability Grid and Helper Banner */}
            <div className="flex flex-col w-full lg:mx-0 lg:px-0 justify-center">
              {/* Get Started */}
              {users.length === 0 && (
                <HelperBanner
                  variant="success"
                  title="Get Started"
                  message="Enter a name in the panel on the right and click Join to add yourself or others. You can then mark your availability on the grid."
                />
              )}

              {/* Schedule Event */}
              {users.length > 0 && isScheduleEventMode && (
                <HelperBanner
                  variant="info"
                  title="Schedule Event"
                  message="Select a time slot on the grid, then click Schedule to add the event to your calendar."
                  actionButton={{
                    label: 'Schedule',
                    onClick: handleScheduleEventComplete,
                    variant: 'primary',
                  }}
                  cancelButton={{
                    label: 'Cancel',
                    onClick: () => {
                      setIsScheduleEventMode(false);
                      interactions.setScheduleEventSelection(null);
                    },
                  }}
                />
              )}

              {/* Overlapping Times */}
              {users.length > 0 && !isScheduleEventMode && isOverlapTimes && (
                <HelperBanner
                  variant="info"
                  title="Overlapping Times"
                  message="View when participants are available. Dark Green: Full Overlap, Light Green: Partial Overlap, and Yellow: No Overlap."
                />
              )}

              {/* Availability */}
              {users.length > 0 &&
                !isScheduleEventMode &&
                !isOverlapTimes &&
                interactions.canEdit && (
                  <HelperBanner
                    variant="success"
                    title="Availability"
                    message="Select a time slot on the grid by clicking and dragging. Erase a cell by clicking on a cell you've already marked."
                  />
                )}

              {/* Viewing */}
              {users.length > 0 &&
                !isScheduleEventMode &&
                !isOverlapTimes &&
                !interactions.canEdit &&
                currentUser && (
                  <HelperBanner
                    variant="neutral"
                    title="Viewing"
                    message={`Viewing ${userIdToName[currentUser] || currentUser}'s availability. You can only make changes to participants you created on this device or linked devices.`}
                  />
                )}

              {/* Availability Grid */}
              <AvailabilityGrid
                actualYmds={actualYmds}
                timezone={timezone}
                ownerTimezone={ownerTimezone}
                hourMode={hourMode}
                gridEnd={hourToTimeString(gridEnd)}
                users={users}
                currentUser={currentUser}
                eventsByUser={eventsByUser}
                isOverlapTimes={isOverlapTimes}
                countsByDateKey={overlap.countsByDateKey}
                totalEligible={overlap.totalEligible}
                TIMES={interactions.TIMES}
                canEdit={interactions.canEdit}
                draftEvent={interactions.draftEvent}
                eraseHover={interactions.eraseHover}
                setHoveredUsers={interactions.setHoveredUsers}
                onDown={interactions.onDown}
                onEnter={interactions.onEnter}
                getUsersAtCell={interactions.getUsersAtCell}
                startHover={isCoarsePointer ? () => {} : startHover}
                stopHover={isCoarsePointer ? () => {} : stopHover}
                moveHover={isCoarsePointer ? () => {} : moveHover}
                onImmediateEnter={handleImmediateEnter}
                onGridLeaveImmediate={handleGridLeaveImmediate}
                isScheduleEventMode={isScheduleEventMode}
                scheduleEventSelection={interactions.scheduleEventSelection}
                hoveredCell={hoveredCell}
                onCellMouseEnter={handleCellMouseEnter}
                onCellMouseLeave={handleCellMouseLeave}
              />
            </div>

            {/* Right sidebar: user list (Participants) */}
            <div className="flex flex-col gap-4 w-full lg:w-64 shrink-0">
              <label className="flex justify-center gap-2 text-xs text-gray-600">
                <Switch
                  label="Show Overlap"
                  isOn={isOverlapTimes}
                  onChange={function (e) {
                    setIsOverlapTimes(e.target.checked);
                  }}
                />
              </label>

              <ParticipantsPanel
                users={users}
                currentUser={currentUser}
                userIdToName={userIdToName}
                ownerByUserId={ownerByUserId}
                ownsParticipant={ownsParticipant}
                joinName={joinName}
                isOverlapTimes={isOverlapTimes}
                hoveredUsers={interactions.hoveredUsers}
                maxParticipants={maxParticipants}
                setCurrentUser={setCurrentUser}
                setJoinName={setJoinName}
                eventsByUser={eventsByUser}
                onRemoveUser={handleRemoveUser}
                onRenameUser={(userId, newName) =>
                  handleRenameUser(userId, newName)
                }
                onJoin={handleJoin}
              />
            </div>
          </div>

          {/* Availability summary below the heatmap and timezone controls */}
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-start gap-4 lg:gap-6">
            <div className="flex flex-col gap-4 w-full py-4 lg:mx-0 lg:px-0">
              {/* Availability summary */}
              <div className="mx-auto">
                <AvailabilityLegendAndHover
                  users={users}
                  userIdToName={userIdToName}
                  eventsByUser={eventsByUser}
                  hoverCell={hoverCell}
                  getUsersAtCell={interactions.getUsersAtCell}
                  isOverlapTimes={isOverlapTimes}
                />
              </div>

              {/* Timezone + Hour Mode Selectors */}
              <div className="mx-auto">
                <ScheduleTimezoneControls
                  timezone={timezone}
                  initialTZ={initialTZ}
                  hourMode={hourMode}
                  onTimezoneChange={(tz) => setTimezone(tz)}
                  onResetTimezone={resetTimezone}
                  onHourModeChange={(m) => setHourMode(m)}
                />
              </div>
            </div>
            <div className="w-full lg:w-64 shrink-0"></div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Tooltip bubble */}
      <HoverTooltip
        hoverInfo={hoverInfo}
        disabled={isCoarsePointer}
        ymdByDateKey={ymdByDateKey}
        getYmd={getYmd}
        TIMES={TIMES}
        gridStart={hourToTimeString(gridStart)}
        gridEnd={hourToTimeString(gridEnd)}
        hourMode={hourMode}
        ownerTimezone={ownerTimezone}
        timezone={timezone}
      />

      {/* Edit event modal */}
      <EventModal
        isOpen={showPopup}
        isOwner={isOwner}
        title={eventTitle}
        onTitleChange={setEventTitle}
        timezone={eventTimezone}
        onTimezoneChange={setEventTimezone}
        pickerRange={pickerRange}
        onPickerRangeChange={setPickerRange}
        pickerStart={pickerStart}
        onPickerStartChange={setPickerStart}
        pickerEnd={pickerEnd}
        onPickerEndChange={setPickerEnd}
        onClose={handleCancelEventModal}
        onSave={debouncedModalSave}
      />

      {/* Schedule event modal */}
      <ScheduleEventModal
        isOpen={showScheduleModal}
        onClose={handleCloseScheduleModal}
        eventTitle={title}
        eventBlock={interactions.scheduleEventSelection}
        ymdByDateKey={ymdByDateKey}
        getYmd={getYmd}
        TIMES={TIMES}
        gridStart={hourToTimeString(gridStart)}
        gridEnd={hourToTimeString(gridEnd)}
        hourMode={hourMode}
        timezone={timezone}
        ownerTimezone={ownerTimezone}
      />

      {/* Device linking modal */}
      <DeviceLinkingModal
        isOpen={showLinkDevicesModal}
        onClose={() => setShowLinkDevicesModal(false)}
      />
    </div>
  );
}

export default App;
